import { useContext, useEffect, useRef, useState } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";
/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * useNavigationGuard Hook
 *
 * Intercepts navigation events (push, replace, and browser unload)
 * to prevent leaving the current page when there are unsaved changes.
 * Displays a confirmation flow and executes a pending navigation
 * only after user confirmation.
 *
 * @param {Object} params - Hook configuration
 * @param {boolean} params.when - Enables or disables the navigation guard
 * @param {Function} [params.onConfirmLeave] - Callback executed when the user confirms navigation
 * @param {Array<string>} [params.allowedPaths=[]] - List of path prefixes that bypass the guard
 *
 * @returns {Object} Navigation guard state and handlers
 * @returns {boolean} returns.open - Indicates whether the confirmation modal should be displayed
 * @returns {Function} returns.handleConfirm - Confirms navigation and executes the pending action
 * @returns {Function} returns.handleCancel - Cancels navigation and keeps the user on the current page
 */
const useNavigationGuard = ({
                                when,
                                onConfirmLeave,
                                allowedPaths = []
                            }) => {

    const { navigator } = useContext(UNSAFE_NavigationContext);
    const [open, setOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const ignoreNextUnload = useRef(false);

    useEffect(() => {
        if (!when) return;

        const originalPush = navigator.push;
        const originalReplace = navigator.replace;

        /**
         * Validates whether a destination path is allowed without triggering the guard.
         *
         * @param {string|Object} to - Target navigation destination (string path or location object)
         * @returns {boolean} True if the path is allowed, otherwise false
         */
        const isAllowedDestination = (to) => {
            if (!to) return false;

            const path = typeof to === "string" ? to : to.pathname;

            return allowedPaths.some((allowed) =>
                path.startsWith(allowed)
            );
        };

        /**
         * Overrides navigator.push to intercept navigation attempts.
         * If the destination is not allowed, stores the pending action
         * and opens the confirmation modal.
         */
        navigator.push = (...args) => {
            const to = args[0];

            if (isAllowedDestination(to)) {
                originalPush(...args);
                return;
            }

            setPendingAction(() => () => originalPush(...args));
            setOpen(true);
        };

        /**
         * Overrides navigator.replace to intercept navigation attempts.
         * Behaves similarly to push interception.
         */
        navigator.replace = (...args) => {
            const to = args[0];

            if (isAllowedDestination(to)) {
                originalReplace(...args);
                return;
            }

            setPendingAction(() => () => originalReplace(...args));
            setOpen(true);
        };

        /**
         * Cleanup: restores original navigator methods
         */
        return () => {
            navigator.push = originalPush;
            navigator.replace = originalReplace;
        };
    }, [navigator, when, allowedPaths]);


    useEffect(() => {
        /**
         * Handles browser tab close / refresh events.
         * Prevents unload when there are unsaved changes.
         *
         * @param {BeforeUnloadEvent} e - Browser event
         */
        const handleBeforeUnload = (e) => {
            if (!when) return;

            if (ignoreNextUnload.current) {
                ignoreNextUnload.current = false;
                return;
            }

            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handleBeforeUnload);

        /**
         * Cleanup: removes event listener
         */
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [when]);

    /**
     * Confirms navigation:
     * - Executes optional callback
     * - Runs the pending navigation action
     * - Resets state
     */
    const handleConfirm = () => {
        onConfirmLeave?.();

        if (pendingAction) {
            pendingAction();
        }

        setOpen(false);
        setPendingAction(null);
    };

    /**
     * Cancels navigation:
     * - Prevents immediate unload trigger
     * - Closes modal
     * - Clears pending action
     */
    const handleCancel = () => {
        ignoreNextUnload.current = true;

        setOpen(false);
        setPendingAction(null);
    };

    return {
        open,
        handleConfirm,
        handleCancel
    };
};

export default useNavigationGuard;
